import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiRequest } from "../../helpers/api";
import { AdminTableSkeleton } from "../../components/skeleton/PageSkeletons";
import { showError, showSuccess } from "../../components/ToastNotifications";
import { useAdminAccess } from "../AdminAccessContext";
import { SortHeader, pageWindow } from "./adminTableParts";
import ContactSubmissionQuickView, { kindLabels, statusLabels } from "./ContactSubmissionQuickView";
import { dateOnly, dateTime, relativeTime } from "./userFormatting";
import "./AdminUsers.css";
import "./ContactSubmissions.css";

const pageSizes = [10, 20, 50, 100];
// Everything that shapes the list lives in the URL, so a filtered view or an open Quick View can be shared.
const panelFilters = ["kind", "assignedTo", "from", "to"];
const sortOptions = [["createdAt", "Submission date"], ["name", "Name"], ["email", "Email"], ["status", "Status"]];

const AdminContactSubmissions = () => {
  const [params, setParams] = useSearchParams();
  const { can } = useAdminAccess();
  const canExport = can("admin.contact_submissions.export");
  const page = Math.max(1, Number.parseInt(params.get("page"), 10) || 1);
  const limit = pageSizes.includes(Number(params.get("limit"))) ? Number(params.get("limit")) : 20;
  const sort = sortOptions.some(([field]) => field === params.get("sort")) ? params.get("sort") : "createdAt";
  const direction = params.get("direction") === "ASC" ? "ASC" : params.get("direction") === "DESC" || sort === "createdAt" ? "DESC" : "ASC";
  const quickView = params.get("submission");
  const status = statusLabels[params.get("status")] ? params.get("status") : "";

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(() => panelFilters.some((key) => params.get(key)));
  // Typing is kept locally and written to the URL after a pause, so each keystroke doesn't trigger a request.
  const [searchText, setSearchText] = useState(params.get("search") || "");
  const currentRequest = useRef(0);

  // Building on a ref keeps both changes when two updates land in one tick (see Users.js).
  const latestParams = useRef(params);
  latestParams.current = params;
  const update = useCallback((changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(latestParams.current);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    if (resetPage) next.delete("page");
    latestParams.current = next;
    setParams(next, { replace: true });
  }, [setParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((params.get("search") || "") !== searchText.trim()) update({ search: searchText.trim() });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchText, params, update]);

  // Dates are sent as the start and end of the day on this admin's clock, matching the dates shown in the table.
  const filterQuery = useMemo(() => {
    const query = new URLSearchParams({ sort, direction });
    ["search", "kind", "assignedTo"].forEach((key) => { if (params.get(key)) query.set(key, params.get(key)); });
    if (status) query.set("status", status);
    const from = new Date(`${params.get("from")}T00:00:00`);
    const to = new Date(`${params.get("to")}T00:00:00`);
    if (!Number.isNaN(from.getTime())) query.set("from", from.toISOString());
    if (!Number.isNaN(to.getTime())) {
      to.setDate(to.getDate() + 1);
      query.set("to", to.toISOString());
    }
    return query;
  }, [params, sort, direction, status]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const requestId = ++currentRequest.current;
    if (!quiet) setLoading(true);
    setError("");
    try {
      const query = new URLSearchParams(filterQuery);
      query.set("page", String(page));
      query.set("limit", String(limit));
      const nextResult = await apiRequest(`/api/admin/contact-submissions?${query}`);
      if (requestId === currentRequest.current) setResult(nextResult);
    } catch (requestError) {
      if (requestId === currentRequest.current) setError(requestError.message || "Unable to load contact submissions.");
    } finally {
      if (requestId === currentRequest.current) setLoading(false);
    }
  }, [filterQuery, page, limit]);

  useEffect(() => {
    load();
    return () => { currentRequest.current += 1; };
  }, [load]);

  // Deleting the only row on the last page would otherwise strand the admin on an empty page.
  const lastPage = result?.pagination?.totalPages;
  useEffect(() => {
    if (!loading && lastPage && page > lastPage) update({ page: lastPage }, { resetPage: false });
  }, [loading, lastPage, page, update]);

  const changeSort = (field) => update(sort === field ? { sort: field, direction: direction === "ASC" ? "DESC" : "ASC" } : { sort: field, direction: field === "createdAt" ? "DESC" : "ASC" });
  const clearFilters = () => {
    setSearchText("");
    update(Object.fromEntries([...panelFilters, "search"].map((key) => [key, null])));
  };
  const closeQuickView = useCallback(() => update({ submission: null }, { resetPage: false }), [update]);
  const refreshQuietly = useCallback(() => load({ quiet: true }), [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { csv, count } = await apiRequest(`/api/admin/contact-submissions/export?${filterQuery}`);
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `contact-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      showSuccess(`Exported ${count.toLocaleString()} ${count === 1 ? "submission" : "submissions"}.`);
    } catch (requestError) {
      showError(requestError.message || "Unable to export contact submissions.");
    } finally {
      setExporting(false);
    }
  };

  const activeFilters = panelFilters.filter((key) => params.get(key)).length;
  const filtered = activeFilters > 0 || Boolean(searchText) || Boolean(status);
  const pagination = result?.pagination;
  const totalPages = Math.max(1, pagination?.totalPages || 1);
  const submissions = result?.submissions || [];
  const counts = result?.statusCounts;
  const assignees = result?.assignees || [];
  const allCount = counts ? counts.new + counts.in_progress + counts.resolved : null;
  const tabs = [["", "All", allCount], ...Object.entries(statusLabels).map(([value, label]) => [value, label, counts?.[value]])];

  return (
    <section className="workspace-dashboard admin-users-page contact-submissions-page">
      <header className="workspace-dashboard-header">
        <div>
          <p className="workspace-eyebrow">WEBSITE INQUIRIES</p>
          <h1>Contact submissions</h1>
          <p className="workspace-intro">Everything sent through the website's contact and demo forms. Open Quick View to assign, update or add notes.</p>
        </div>
        <div className="admin-directory-links">
          {canExport && <button type="button" className="admin-create-user-button" onClick={exportCsv} disabled={exporting || !pagination?.total}>
            <i className={`ti ${exporting ? "ti-loader-2 is-spinning" : "ti-file-export"}`} aria-hidden="true" /> {exporting ? "Exporting…" : "Export CSV"}
          </button>}
        </div>
      </header>

      <section className="workspace-panel admin-users-panel">
        <div className="contact-status-tabs" role="group" aria-label="Filter by status">
          {tabs.map(([value, label, count]) => (
            <button key={label} type="button" aria-pressed={status === value} onClick={() => update({ status: value })}>
              {label}{count !== null && count !== undefined && <span>{count.toLocaleString()}</span>}
            </button>
          ))}
        </div>
        <div className="admin-users-toolbar">
          <label className="admin-users-search">
            <i className="ti ti-search" aria-hidden="true" />
            <input aria-label="Search submissions" type="search" placeholder="Search name, email, company or message" value={searchText} onChange={(event) => setSearchText(event.target.value)} />
          </label>
          <div className="admin-users-toolbar-actions">
            <button type="button" className={`admin-filter-toggle${showFilters ? " is-open" : ""}`} aria-expanded={showFilters} aria-controls="contact-submission-filters" onClick={() => setShowFilters((value) => !value)}>
              <i className="ti ti-adjustments-horizontal" aria-hidden="true" /> Filters{activeFilters ? <span className="admin-filter-count">{activeFilters}</span> : null}
            </button>
            <label className="admin-users-sort">
              <span className="visually-hidden">Sort by</span>
              <select aria-label="Sort by" value={`${sort}:${direction}`} onChange={(event) => { const [field, dir] = event.target.value.split(":"); update({ sort: field, direction: dir }); }}>
                {sortOptions.flatMap(([field, label]) => [
                  <option key={`${field}:DESC`} value={`${field}:DESC`}>{label} {field === "createdAt" ? "(newest)" : field === "status" ? "(resolved first)" : "(Z–A)"}</option>,
                  <option key={`${field}:ASC`} value={`${field}:ASC`}>{label} {field === "createdAt" ? "(oldest)" : field === "status" ? "(new first)" : "(A–Z)"}</option>
                ])}
              </select>
            </label>
            <button type="button" className="admin-icon-button" onClick={() => load()} disabled={loading} aria-label="Refresh list"><i className="ti ti-refresh" aria-hidden="true" /></button>
          </div>
        </div>
        {showFilters && <div className="admin-user-filters" id="contact-submission-filters">
          <label>Type
            <select aria-label="Type" value={params.get("kind") || ""} onChange={(event) => update({ kind: event.target.value })}>
              <option value="">All types</option>
              {Object.entries(kindLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Assigned to
            <select aria-label="Assigned to" value={params.get("assignedTo") || ""} onChange={(event) => update({ assignedTo: event.target.value })}>
              <option value="">Anyone</option>
              <option value="unassigned">Unassigned</option>
              {assignees.map((admin) => <option key={admin.id} value={String(admin.id)}>{admin.name}</option>)}
            </select>
          </label>
          <label>Submitted from<input aria-label="Submitted from" type="date" value={params.get("from") || ""} max={params.get("to") || undefined} onChange={(event) => update({ from: event.target.value })} /></label>
          <label>Submitted to<input aria-label="Submitted to" type="date" value={params.get("to") || ""} min={params.get("from") || undefined} onChange={(event) => update({ to: event.target.value })} /></label>
          <button className="admin-filter-clear" type="button" onClick={clearFilters} disabled={!activeFilters && !searchText}>Clear all</button>
        </div>}

        {error && <div className="admin-users-error" role="alert"><span>{error}</span><button type="button" onClick={() => load()} disabled={loading}>Retry</button></div>}
        {loading && !result ? <AdminTableSkeleton label="Loading contact submissions" columns={7} /> : !error && !submissions.length ? (
          <div className="workspace-empty admin-users-empty"><strong>No submissions found</strong><span>{filtered ? "Try changing your search or filters." : "Messages from the website's contact form will appear here."}</span>{(activeFilters > 0 || searchText) && <button type="button" className="admin-filter-clear" onClick={clearFilters}>Clear search and filters</button>}</div>
        ) : !error && <>
          <div className={`admin-users-table-wrap${loading ? " is-refreshing" : ""}`} aria-busy={loading || undefined}>
            <table className="admin-users-table">
              <thead><tr>
                <SortHeader field="name" label="From" sort={sort} direction={direction} onSort={changeSort} />
                <th>Message</th>
                <th>Type</th>
                <SortHeader field="status" label="Status" sort={sort} direction={direction} onSort={changeSort} />
                <th>Assigned to</th>
                <SortHeader field="createdAt" label="Submitted" sort={sort} direction={direction} onSort={changeSort} />
                <th><span className="visually-hidden">Actions</span></th>
              </tr></thead>
              <tbody>{submissions.map((item) => (
                <tr key={item.id} className={String(item.id) === quickView ? "is-selected" : undefined}>
                  <td data-label="From"><div className="admin-user-name"><span aria-hidden="true">{item.name.slice(0, 1).toUpperCase()}</span><div><strong>{item.name}</strong><small>{item.email}</small></div></div></td>
                  <td data-label="Message"><div className="contact-message-cell"><span>{item.messagePreview || <span className="admin-muted">No message</span>}</span>{item.company && <small>{item.company}</small>}</div></td>
                  <td data-label="Type">{kindLabels[item.kind]}</td>
                  <td data-label="Status"><span className={`admin-status-badge contact-status is-${item.status}`}>{statusLabels[item.status]}</span></td>
                  <td data-label="Assigned to">{item.assignee?.name || <span className="admin-muted">Unassigned</span>}</td>
                  <td data-label="Submitted"><div className="admin-cell-stack"><span title={dateTime(item.createdAt)}>{dateOnly(item.createdAt)}</span><small>{relativeTime(item.createdAt)}</small></div></td>
                  <td className="admin-row-actions-cell"><button type="button" className="admin-row-action" onClick={() => update({ submission: item.id }, { resetPage: false })} aria-label={`View submission from ${item.name}`}><i className="ti ti-eye" aria-hidden="true" /> View</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <footer className="admin-users-pagination">
            <span>{pagination.total ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, pagination.total)} of ${pagination.total.toLocaleString()}` : "No submissions"}</span>
            <div>
              <label className="admin-page-size">Rows
                <select aria-label="Rows per page" value={limit} onChange={(event) => update({ limit: event.target.value })}>{pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}</select>
              </label>
              <nav className="admin-page-buttons" aria-label="Pagination">
                <button type="button" onClick={() => update({ page: page - 1 }, { resetPage: false })} disabled={page <= 1 || loading} aria-label="Previous page"><i className="ti ti-chevron-left" aria-hidden="true" /></button>
                {pageWindow(Math.min(page, totalPages), totalPages).map((value, index) => value === "gap"
                  ? <span key={`gap-${index}`} aria-hidden="true">…</span>
                  : <button key={value} type="button" aria-current={value === page ? "page" : undefined} onClick={() => update({ page: value }, { resetPage: false })} disabled={loading}>{value}</button>)}
                <button type="button" onClick={() => update({ page: page + 1 }, { resetPage: false })} disabled={page >= totalPages || loading} aria-label="Next page"><i className="ti ti-chevron-right" aria-hidden="true" /></button>
              </nav>
            </div>
          </footer>
        </>}
      </section>
      {quickView && <ContactSubmissionQuickView key={quickView} submissionId={quickView} assignees={assignees} onClose={closeQuickView} onChanged={refreshQuietly} />}
    </section>
  );
};

export default AdminContactSubmissions;
